import { useEffect, useMemo, useState } from 'react';
import { procurementApi, type PurchaseOrderResponse } from '@/api/procurement.api';
import type { SelectOption } from '@/components/ui/Select';
import { formatPurchaseOrderLabel } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function usePurchaseOrderOptions(pageSize = 200) {
  const [rows, setRows] = useState<PurchaseOrderResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    procurementApi
      .listPurchaseOrders({ page: 0, size: pageSize })
      .then((res) => setRows(res.content))
      .catch((err) => showApiError(err, 'Failed to load purchase orders'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () => rows.map((r) => ({ value: String(r.id), label: formatPurchaseOrderLabel(r) })),
    [rows],
  );

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const getPurchaseOrder = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined) => {
    const row = getPurchaseOrder(id);
    if (!row) return id != null && id !== '' ? `PO #${id}` : '—';
    return formatPurchaseOrderLabel(row);
  };

  return { rows, options, loading, getPurchaseOrder, getLabel, byId };
}
